import * as React from 'react';

import { toMoney } from '@rematter/common/money';
import {
  enumCurrencyCode,
  enumPaymentMethod,
  PaymentMethod,
} from '@rematter/frontend-lib-api-client';
import {
  ContentSection,
  DatePicker,
  Grid,
  MoneyField,
  TextField,
  type DateValue,
} from '@rematter/ui-web';
import { useAtomValue, useSetAtom } from 'jotai';

import {
  $currentDraftPaymentInfo,
  $effectivePaymentInfo,
  DraftPaymentInfo,
} from '../../_state/paymentDetail.state';
import { useTranslations } from '../../i18n/useTranslations';
import { FinanceDetailLayout } from '../_layouts/FinanceDetailLayout';

import { PaymentDetailFooter } from './PaymentDetailFooter';

import { SelectEnum } from '~/shared/components/select-fields/SelectEnum';
import { anyCalendarDateToCalendarDate } from '~/shared/utils/dateTime';

type Props = {
  disabled?: boolean;
};

//
// ⚛️ ⚛️ ⚛️ PAYMENT TAB INFO ⚛️ ⚛️ ⚛️
//
export const PaymentTabInfo: React.FC<Props> = ({ disabled = false }) => {
  // Translations
  const t = useTranslations();

  // Atoms
  const effectivePayment = useAtomValue($effectivePaymentInfo);
  const setDraftPaymentInfo = useSetAtom($currentDraftPaymentInfo);

  // Callbacks
  const handleFieldChange = React.useCallback(
    (updates: Partial<DraftPaymentInfo>) => {
      if (!effectivePayment) return;

      setDraftPaymentInfo({
        documentNumber: effectivePayment.documentNumber,
        transactionDate: effectivePayment.transactionDate,
        amount: effectivePayment.amount,
        paymentMethod: effectivePayment.paymentMethod,
        checkNumber: effectivePayment.checkNumber ?? null,
        checkMemo: effectivePayment.checkMemo ?? null,
        achNumber: effectivePayment.achNumber ?? null,
        wireNumber: effectivePayment.wireNumber ?? null,
        ...updates,
      });
    },
    [effectivePayment, setDraftPaymentInfo],
  );

  if (!effectivePayment) {
    return (
      <div className="p-4 text-neutral/60">
        {t.payments.detail.empty.noData}
      </div>
    );
  }

  return (
    <FinanceDetailLayout
      className="@PaymentTabInfo h-auto"
      footer={<PaymentDetailFooter />}
    >
      <ContentSection
        title={t.payments.detail.sections.basicInfo}
        titleClassName="text-lg font-semibold text-neutral/90"
      >
        <Grid gap4 cols3 className="items-start">
          <TextField
            label={t.payments.detail.fields.documentNumber}
            value={effectivePayment.documentNumber ?? ''}
            onChange={(value) =>
              handleFieldChange({ documentNumber: value || null })
            }
            isDisabled={disabled}
          />
          <TextField
            label={t.payments.detail.fields.status}
            value={effectivePayment.status}
            isDisabled
          />
          <TextField
            label={t.payments.detail.fields.paymentType}
            value={
              t.enums.paymentType[effectivePayment.paymentType] ??
              effectivePayment.paymentType
            }
            isDisabled
          />
          <DatePicker
            label={t.payments.detail.fields.transactionDate}
            value={effectivePayment.transactionDate as DateValue | undefined}
            onChange={(value) =>
              handleFieldChange({
                transactionDate: anyCalendarDateToCalendarDate(
                  value ?? undefined,
                ),
              })
            }
            isDisabled={disabled}
          />
          <MoneyField
            label={t.payments.detail.fields.amount}
            amount={effectivePayment.amount?.amount ?? 0}
            currencyCode={
              effectivePayment.amount?.currencyCode ?? enumCurrencyCode.USD
            }
            onAmountChange={(value) =>
              handleFieldChange({
                amount: toMoney({
                  amount: value,
                  currencyCode:
                    effectivePayment.amount?.currencyCode ??
                    enumCurrencyCode.USD,
                }),
              })
            }
            currencyLocked
            isDisabled={disabled}
          />
          <MoneyField
            label={t.payments.detail.fields.unappliedAmount}
            amount={effectivePayment.unappliedAmount.amount ?? 0}
            currencyCode={
              effectivePayment.unappliedAmount.currencyCode ??
              enumCurrencyCode.USD
            }
            currencyLocked
            isDisabled
          />
          <SelectEnum<PaymentMethod>
            label={t.payments.detail.fields.paymentMethod}
            enumValue={enumPaymentMethod}
            value={effectivePayment.paymentMethod ?? undefined}
            onChange={(value) =>
              handleFieldChange({ paymentMethod: value ?? null })
            }
            isDisabled={disabled}
          />
          <TextField
            label={t.payments.detail.fields.account}
            value={effectivePayment.accountName ?? '-'}
            isDisabled
          />
          <TextField
            label={t.payments.detail.fields.financialAccount}
            value={effectivePayment.financialAccount?.name ?? '-'}
            isDisabled
          />
        </Grid>
      </ContentSection>

      {effectivePayment.paymentMethod === 'Check' && (
        <ContentSection
          title={t.payments.detail.sections.checkDetails}
          titleClassName="text-lg font-semibold text-neutral/90"
        >
          <Grid gap4 cols3 className="items-start">
            <TextField
              label={t.payments.detail.fields.checkNumber}
              value={effectivePayment.checkNumber ?? ''}
              onChange={(value) =>
                handleFieldChange({ checkNumber: value || null })
              }
              isDisabled={disabled}
            />
            <div className="col-span-2">
              <TextField
                label={t.payments.detail.fields.checkMemo}
                value={effectivePayment.checkMemo ?? ''}
                onChange={(value) =>
                  handleFieldChange({ checkMemo: value || null })
                }
                isDisabled={disabled}
              />
            </div>
          </Grid>
        </ContentSection>
      )}

      {effectivePayment.paymentMethod === 'ACH' && (
        <ContentSection
          title={t.payments.detail.sections.achDetails}
          titleClassName="text-lg font-semibold text-neutral/90"
        >
          <Grid gap4 cols3 className="items-start">
            <TextField
              label={t.payments.detail.fields.achNumber}
              value={effectivePayment.achNumber ?? ''}
              onChange={(value) =>
                handleFieldChange({ achNumber: value || null })
              }
              isDisabled={disabled}
            />
          </Grid>
        </ContentSection>
      )}

      {effectivePayment.paymentMethod === 'Wire' && (
        <ContentSection
          title={t.payments.detail.sections.wireDetails}
          titleClassName="text-lg font-semibold text-neutral/90"
        >
          <Grid gap4 cols3 className="items-start">
            <TextField
              label={t.payments.detail.fields.wireNumber}
              value={effectivePayment.wireNumber ?? ''}
              onChange={(value) =>
                handleFieldChange({ wireNumber: value || null })
              }
              isDisabled={disabled}
            />
          </Grid>
        </ContentSection>
      )}
    </FinanceDetailLayout>
  );
};
